/*  PDF-RIPORT – a kiszolgálói riport-motor (backend/pdf-riport.js, POST /api/riport-pdf)
 *  felületi oldala.
 *
 *  A riportok PDF-je eddig a böngészőben, jsPDF + autotable-lel készült, a képernyőn álló
 *  táblázatból (`html: '#printable'`). Mostantól a kiszolgáló rendereli, a munkaidő-
 *  elszámolás arculatával (iskola-színek, logók, adatsáv, aláírás). A táblázatot továbbra is
 *  a képernyőről vesszük – ami ott látszik, az kerül a papírra –, csak nem képként és jsPDF-
 *  cellaként, hanem oszlop/sor JSON-ként:
 *    - a rowspan/colspan megmarad (a Riportok egy dolgozója négy sorban áll);
 *    - az ikonok (ion-icon) rövid jelek lesznek (Sz, B, Ki, HO, !…) – a jsPDF-es változat
 *      base64-képeket rajzolt a cellákba;
 *    - a gombok, beviteli mezők kimaradnak, a rejtett (display: none) cellák is;
 *    - a cella saját (inline) háttérszíne – az állapotszín – halvány tónusként megy át.
 */

/** Egy cella a kérésben: szöveg, vagy objektum (lásd backend/pdf-riport.js › cellaNorm). */
export type RiportCella = string | number | {
  t: string; rs?: number; cs?: number; hatter?: string; fej?: boolean; halvany?: boolean;
  igazitas?: 'bal' | 'kozep' | 'jobb';
};
export type RiportSor = RiportCella[] | { csoport: string } | { cellak: RiportCella[]; tipus?: 'osszeg' | 'kiemelt' };
/** `fuggoleges`: a fejléc szövege alulról felfelé áll (keskeny oszlopú kereszttábla). */
export interface RiportOszlop { nev: string; igazitas?: 'bal' | 'kozep' | 'jobb'; szelesseg?: string; fuggoleges?: boolean }
/** `ujOldal`: a tábla új oldalon kezdődik (pl. egy széles tábla oszlop-darabjai). */
export interface RiportTabla {
  cim?: string; osszegzes?: string; oszlopok: RiportOszlop[]; sorok: RiportSor[];
  osszesen?: RiportCella[]; fel?: boolean; kiemelt?: boolean; sorMagassag?: string; ures?: string;
  ujOldal?: boolean;
}
export interface RiportKeres {
  intezmeny: string; cim: string; cimKieg?: string; alcim?: string;
  adatsav?: [string, string][]; tajolas?: 'allo' | 'fekvo'; lapmeret?: 'A4' | 'A3';
  betumeret?: number; alsoHely?: number; tablak: RiportTabla[];
  lablec?: { kelt?: boolean | string; alairok?: string[]; megjegyzes?: string; keszitette?: string } | false;
  fajlnev?: string;
}

/** Az ikonok rövid jele a papíron (a képernyőn ion-icon). */
const IKON_JEL: Record<string, string> = {
  'alert-outline': '!', 'alert-circle-outline': '!', 'alert-circle': '!',
  'checkmark-outline': '✓', 'checkmark-circle-outline': '✓', 'checkmark-circle': '✓',
  'bed': 'Sz', 'bed-outline': 'Sz',
  'medkit': 'B', 'medkit-outline': 'B',
  'car': 'Ki', 'car-outline': 'Ki',
  'globe': 'HO', 'globe-outline': 'HO',
  'ban': 'FN', 'ban-outline': 'FN',
  'leaf': 'Ment', 'leaf-outline': 'Ment',
  'close-circle-outline': '–',
  'hourglass-outline': '…',
};

/** Az állapotjel színe (az Ionic-paletta, a papíron halvány tónusként – lásd pdf-riport.js › tonus). */
const JEL_SZIN: Record<string, string> = {
  'Sz': '#3dc2ff', 'B': '#ffc409', 'Ki': '#5260ff', 'HO': '#5260ff', 'FN': '#92949c', 'Ment': '#2dd36f', '!': '#eb445a',
};

/** A jelek magyarázata a riport aljára. */
export const IKON_JELMAGYARAZAT =
  'Jelek: Sz = szabadság, B = betegszabadság, Ki = kiküldetés, HO = home office / digitális távoktatás, '
  + 'FN = fizetés nélküli szabadság, Ment = egyéb mentesülés, ! = hiányzó bejegyzés.';

/** Pénz, darab, óra – ami jobbra igazítandó (a backend SZAM-mintája). A „08:00” nem az. */
const SZAM = /^[-−+]?\d[\d\s\u00a0.,]*(\s?(Ft|HUF|%|óra|ó|km|nap|db|perc))?$/i;

const rejtett = (el: Element) => {
  try { return getComputedStyle(el).display === 'none'; } catch { return false; }
};

/** rgb(a)(…) → #rrggbb; az átlátszó és a fehér háttér nem szín. */
function hexSzin(css: string): string | undefined {
  const m = String(css || '').match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?/i);
  if (!m) return undefined;
  if (m[4] !== undefined && Number(m[4]) === 0) return undefined;
  const hex = '#' + [m[1], m[2], m[3]].map((x) => Number(x).toString(16).padStart(2, '0')).join('');
  return hex === '#ffffff' ? undefined : hex;
}

/*  Sortörés-jel a szövegben: magánhasználati karakter, mert a `\s` (a szóköz-összevonás)
 *  a U+2028-at is elnyelné. */
const SOR = '\uE000';

/** „512,000 Ft” (a currency-pipe angol tagolása) → „512 000 Ft”, ahogy a magyar papíron áll. */
function magyarFt(s: string): string {
  return /^-?\d{1,3}(,\d{3})+ ?Ft$/.test(s) ? s.replace(/,/g, '\u00a0') : s;
}

/** A cella szövege: ikon → jel, <br> → új sor, gomb/mező nélkül, összevont szóközökkel. */
function cellaSzoveg(cell: HTMLElement, jelek: Record<string, string> = IKON_JEL): string {
  //  Az ikon nevét az EREDETI elemről olvassuk: a `[name]="…"` kötés tulajdonság, a klónra
  //  nem kerül át.
  const nevek = Array.from(cell.querySelectorAll('ion-icon')).map((ic: any) => String(ic.name || ic.getAttribute('name') || ''));
  const klon = cell.cloneNode(true) as HTMLElement;
  Array.from(klon.querySelectorAll('ion-icon')).forEach((ic, i) => {
    const gombban = ic.closest('ion-button, button');
    ic.replaceWith(document.createTextNode(gombban ? '' : ` ${jelek[nevek[i]] ?? ''} `));
  });
  klon.querySelectorAll('ion-button, button, input, select, textarea, ion-input, ion-select, ion-checkbox, script, style')
    .forEach((e) => e.remove());
  klon.querySelectorAll('br').forEach((br) => br.replaceWith(document.createTextNode(SOR)));
  return String(klon.textContent || '')
    .replace(/\s+/g, ' ')
    .split(SOR).map((s) => magyarFt(s.trim())).filter(Boolean)
    .join('\n');
}

/**
 *  A képernyőn álló táblázat oszlop/sor JSON-ként.
 *  @param kihagy  a fejléc-szöveg alapján kihagyandó oszlopok (pl. „Műveletek”)
 *  @param ikonJel az ikon-jelek felülírása (pl. a Munkábajárásnál a tiltójel nem „FN”)
 *  A csupa számot (pénz, darab) tartalmazó oszlop jobbra igazítást kap.
 */
export function domTablaKinyer(table: HTMLTableElement | null,
  opc: { kihagy?: (nev: string) => boolean; ikonJel?: Record<string, string> } = {}):
  { oszlopok: RiportOszlop[]; sorok: RiportSor[] } {
  if (!table) return { oszlopok: [], sorok: [] };
  const jelek = { ...IKON_JEL, ...(opc.ikonJel || {}) };
  const fejSorok = Array.from(table.tHead?.rows || []).filter((r) => !rejtett(r));
  const fejsor = fejSorok[fejSorok.length - 1];
  const nevek: string[] = [];
  for (const th of Array.from(fejsor?.cells || [])) {
    if (rejtett(th)) continue;
    const n = cellaSzoveg(th as HTMLElement, jelek);
    for (let i = 0; i < Math.max(1, (th as HTMLTableCellElement).colSpan || 1); i += 1) nevek.push(n);
  }
  const kihagyott = new Set<number>();
  nevek.forEach((n, i) => { if (opc.kihagy?.(n)) kihagyott.add(i); });
  const oszlopok: RiportOszlop[] = nevek.filter((_, i) => !kihagyott.has(i)).map((nev) => ({ nev }));
  const oszlopDb = nevek.length;

  const sorok: RiportSor[] = [];
  const foglalt: Set<number>[] = [];
  const nemSzam = new Set<number>();     // logikai oszlop, amiben van nem szám érték
  const vanErtek = new Set<number>();
  const torzsSorok = Array.from(table.tBodies).flatMap((tb) => Array.from(tb.rows)).filter((r) => !rejtett(r));
  torzsSorok.forEach((tr, idx) => {
    foglalt[idx] = foglalt[idx] || new Set<number>();
    let oszlop = 0;
    const cellak: RiportCella[] = [];
    for (const c of Array.from(tr.cells) as HTMLTableCellElement[]) {
      if (rejtett(c)) continue;
      while (foglalt[idx].has(oszlop)) oszlop += 1;
      const rs = Math.max(1, c.rowSpan || 1);
      const cs = Math.max(1, c.colSpan || 1);
      for (let r = 0; r < rs; r += 1) {
        foglalt[idx + r] = foglalt[idx + r] || new Set<number>();
        for (let k = 0; k < cs; k += 1) foglalt[idx + r].add(oszlop + k);
      }
      let marad = 0;
      for (let k = 0; k < cs; k += 1) if (!kihagyott.has(oszlop + k)) marad += 1;
      oszlop += cs;
      if (!marad) continue;
      //  Csak a cella SAJÁT (inline) háttere számít – a téma háttere (sötét mód!) nem.
      const sajatHatter = c.style.background || c.style.backgroundColor;
      const hatter = sajatHatter ? hexSzin(getComputedStyle(c).backgroundColor) : undefined;
      const cella: any = { t: cellaSzoveg(c, jelek) };
      if (cella.t && cs === 1) {
        vanErtek.add(oszlop - 1);
        if (!SZAM.test(cella.t)) nemSzam.add(oszlop - 1);
      }
      if (rs > 1) cella.rs = rs;
      if (marad > 1) cella.cs = marad;
      //  A csak állapotjelet tartalmazó cella (a Riportok napjai) saját szín nélkül is tónust kap.
      if (hatter) cella.hatter = hatter;
      else if (JEL_SZIN[cella.t] && c.querySelector('ion-icon')) cella.hatter = JEL_SZIN[cella.t];
      if (c.tagName === 'TH') cella.fej = true;
      cellak.push(cella);
    }
    //  Egyetlen, teljes szélességű cella: elválasztó/összegző sor (pl. „Heti összesített óraszám”).
    const egy = cellak.length === 1 ? cellak[0] as any : null;
    if (egy && (egy.cs || 1) >= oszlopok.length && oszlopDb > 1) sorok.push({ csoport: egy.t });
    else sorok.push(cellak);
  });
  let j = 0;
  for (let i = 0; i < oszlopDb; i += 1) {
    if (kihagyott.has(i)) continue;
    if (vanErtek.has(i) && !nemSzam.has(i)) oszlopok[j].igazitas = 'jobb';
    j += 1;
  }
  return { oszlopok, sorok };
}

/** „2026. október” */
export function honapCimke(ev: number | string, ho: number | string): string {
  const nevek = ['január', 'február', 'március', 'április', 'május', 'június', 'július',
    'augusztus', 'szeptember', 'október', 'november', 'december'];
  return `${ev}. ${nevek[Number(ho) - 1] || ho}`;
}

/** „512 000 Ft” → 512000 (ha nem Ft-összeg: null). */
function ftErtek(v: any): number | null {
  const s = String(v ?? '').trim();
  if (!/Ft$/.test(s)) return null;
  //  Forintban egész összegek állnak; a tagolás (szóköz, vessző, pont) bármi lehet.
  const n = Number(s.replace(/Ft$/, '').replace(/[^\d-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 *  Összesen-sor az egyszerű (rowspan nélküli) táblához: ahol az oszlop minden nem üres
 *  értéke Ft-összeg, ott az összeg, máshol üres. Ha egyetlen pénzoszlop sincs: null.
 */
export function ftOsszesen(oszlopok: RiportOszlop[], sorok: RiportSor[], cimke = 'Összesen'): RiportCella[] | null {
  const tomb = sorok.filter((s): s is RiportCella[] => Array.isArray(s));
  if (tomb.some((s) => s.some((c: any) => typeof c === 'object' && ((c.rs || 1) > 1 || (c.cs || 1) > 1)))) return null;
  const szoveg = (c: any) => (c && typeof c === 'object' ? c.t : c);
  let van = false;
  const sor: RiportCella[] = oszlopok.map((_, i) => {
    if (i === 0) return cimke;
    const ertekek = tomb.map((s) => szoveg(s[i])).filter((v) => String(v ?? '').trim());
    const szamok = ertekek.map(ftErtek);
    if (!ertekek.length || szamok.some((x) => x === null)) return '';
    van = true;
    return `${Math.round(szamok.reduce((a: number, b) => a + (b as number), 0)).toLocaleString('hu-HU')} Ft`;
  });
  return van ? sor : null;
}
